// v2 saguaro — a genuinely ribbed desert column: a tapering core sheathed in six
// vertical rib ridges that lean in toward a rounded crown, and arms that spring
// from the trunk as real elbows — a tube bowing out and up into an upright
// forearm carrying its own outer rib and rounded tip — finished with a crown of
// waxy white saguaro blooms on the trunk top and the first arm. Variants re-deal
// the arm count, attach heights and yaws and spin the rib cage. Ported 1:1 from
// age-of-mcp's v2/cactus-saguaro.ts, using the library's shared `tropic` (h01,
// lerp, tube) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part } from "../../lib";

const CORE = "#44854c";
const RIB = "#34703e";
const RIB_2 = "#2c6136";
const CROWN = "#4f9655";
const ARM = "#3f7d47";
const ARM_RIB = "#316a3b";
const BLOOM = "#f2ecd4";

function saguaroParts(v: number): Part[] {
  const seed = v * 17 + 5;
  const yaw = v * 2.2 + tropic.h01(seed, 0) * 0.9;
  const parts: Part[] = [
    // tapering core column
    { geometry: new THREE.CylinderGeometry(0.105, 0.15, 1.24, 8),
      color: CORE, position: [0, 0.62, 0] },
    // rounded crown
    { geometry: new THREE.IcosahedronGeometry(0.125, 0), color: CROWN,
      position: [0, 1.26, 0], rotation: [0, yaw, 0], scale: [1, 0.8, 1] },
  ];
  // Six vertical rib ridges sheathing the core, tops leaning inward.
  for (let i = 0; i < 6; i++) {
    const a = yaw + (i / 6) * Math.PI * 2;
    parts.push({
      geometry: new THREE.CylinderGeometry(0.026, 0.04, 1.12, 5),
      color: i % 2 ? RIB : RIB_2,
      position: [Math.cos(a) * 0.105, 0.57, Math.sin(a) * 0.105],
      rotation: [Math.sin(a) * -0.055, 0, Math.cos(a) * 0.055],
    });
  }
  // Arms — each a real elbow: a tube bowing out of the trunk, an upright
  // forearm with its own rib ridge, and a rounded tip.
  const nArms = 2 + (v % 2);
  for (let i = 0; i < nArms; i++) {
    const a = yaw + (i / nArms) * Math.PI * 2 + 0.7 + tropic.h01(seed, 10 + i) * 0.8;
    const ay = tropic.lerp(0.4, 0.62, tropic.h01(seed, 20 + i));     // attach height
    const armH = tropic.lerp(0.32, 0.5, tropic.h01(seed, 30 + i));   // forearm length
    const ex = Math.cos(a) * 0.31, ez = Math.sin(a) * 0.31;
    parts.push(tropic.tube([Math.cos(a) * 0.08, ay, Math.sin(a) * 0.08],
      [ex, ay + 0.16, ez], 0.072, 0.06, ARM, 6));
    parts.push(tropic.tube([ex, ay + 0.12, ez],
      [ex + 0.015, ay + 0.12 + armH, ez], 0.062, 0.052, ARM, 6));
    parts.push(tropic.tube(
      [ex + Math.cos(a) * 0.052, ay + 0.2, ez + Math.sin(a) * 0.052],
      [ex + Math.cos(a) * 0.052, ay + 0.06 + armH, ez + Math.sin(a) * 0.052],
      0.015, 0.011, ARM_RIB, 4));
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.06, 0),
      color: CROWN, position: [ex + 0.015, ay + 0.14 + armH, ez],
      scale: [1, 0.85, 1] });
    if (i === 0) {                       // a bloom crowns the first arm
      parts.push({ geometry: new THREE.IcosahedronGeometry(0.035, 0),
        color: BLOOM, position: [ex + 0.015, ay + 0.2 + armH, ez] });
    }
  }
  // Waxy white bloom crown ringing the trunk top.
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.5 + (i / 3) * Math.PI * 2 + tropic.h01(seed, 40 + i) * 0.5;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.038, 0),
      color: BLOOM,
      position: [Math.cos(a) * 0.07, 1.345, Math.sin(a) * 0.07] });
  }
  return parts;
}

export const cactusSaguaroV2 = defineModel({
  id: "cactus-saguaro-v2",
  batchId: "fable",
  name: "Cactus saguaro (v2)",
  tags: ["scenery","cactus","saguaro","low-poly","age-of-mcp","fable"],
  summary: "Low-poly cactus saguaro.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.36,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => saguaroParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => saguaroParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => saguaroParts(2) },
  ],
  animations: [],
});
